import { Avatar, Button, DatePicker, Flex, Form, Input, Select, Tooltip, Typography } from "antd";
import { useMessageToast } from "../Layout/DefaultLayout";
import { ScheduledMessage, ScheduleMessage, useScheduleMessage, useUpdateScheduledMessage } from "../../hooks/apis/scheduled_messages.api";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { formatPhoneNumber, phoneNumberFormatE164, phoneNumberValidate } from "../../utils/phoneLib";
import { didlabelStyle } from "../../pages/BulkMessages";
import { parsePhoneNumber } from "libphonenumber-js";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import TextArea from "antd/es/input/TextArea";
import { addScheduledMessageSlice, updateScheduledMessageSlice } from "../../store/slices/scheduled_messages.slice";
import { disabledTime } from "../../utils/date-time-picker";
import { generateNameSpaceColor } from "../../utils/color";
import { Contact } from "@data-phone/react-generic/dist/components/Forms/Contacts";
import { formatContactName } from "../../utils/contacts";
import styled from "styled-components";

const InputWrapper = styled.div`
  position: relative;
`;


const ContactDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow: auto;
`;

const ContactItem = styled.div`
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const StyledAvatar = styled(Avatar)`
  color: black;
  margin-right: 15px;
`;

const ContactName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const ContactPhone = styled.div`
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgb(119, 117, 135);
`;


type MyContact = Contact & {
  searchableName: string;
};

export const ScheduleForm = ({ setIsModalOpen, editData, did, participant }: { setIsModalOpen: Function; editData?: ScheduledMessage; did?: string; participant?: string }) => {
  const [form] = Form.useForm();
  const dispatch: AppDispatch = useDispatch();

  const { messageApi } = useMessageToast();
  const { mutate: scheduleMessage, isLoading: isLoadingScheduleMessage } = useScheduleMessage();
  const { mutate: updateScheduledMessage, isLoading: isLoadingUpdate } = useUpdateScheduledMessage();

  const { data: smsDids } = useSelector((state: RootState) => state.smsDids);
  const { data: contactsRaw } = useSelector((state: RootState) => state.contacts);

  const [number, setNumber] = useState("");
  const [flagIcon, setFlagIcon] = useState<string>("US");
  const [showContacts, setShowContacts] = useState(false);

  const contacts = useMemo(() => {
    return contactsRaw.map((contact: Contact) => {
      return {
        ...contact,
        searchableName: formatContactName(contact).toLowerCase(),
      }
    })
      .sort((a, b) => {
        const nameA = a.searchableName;
        const nameB = b.searchableName;
        return nameA.localeCompare(nameB);
      });
  }, [contactsRaw]);

  const filteredContacts = useMemo(() => {
    if (!number?.trim()) return contacts;

    const query = number.toLowerCase().trim();
    const queryDigits = query.replace(/\D/g, '');
    const results = new Set<MyContact>();

    if (query.length >= 2) {
      for (const contact of contacts) {
        if (contact.searchableName.includes(query)) {
          results.add(contact);
        }
      }

      if (queryDigits.length >= 3) {
        contacts.forEach((contact) => {
          if (contact.phones.some(p => p.phone_number.includes(queryDigits))) {
            results.add(contact);
          }
        });
      }
    }

    return Array.from(results).sort((a, b) => {
      const nameA = a.searchableName;
      const nameB = b.searchableName;
      return nameA.localeCompare(nameB);
    });
  }, [number, contacts]);


  const handleContactSelect = (contact: any) => {
    const phoneNumber = contact.phones[0]?.phone_number;
    if (phoneNumberValidate(phoneNumber)) {
      setNumber(phoneNumber);
      form.setFieldValue("participant", formatPhoneNumber(phoneNumber));
      setShowContacts(false);
    } else {
      messageApi.error("Invalid Phone Number");
    }
  };


  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        did: formatPhoneNumber(editData.did),
        participant: formatPhoneNumber(editData.participant),
        body: editData.body,
        scheduled_at: dayjs(new Date(editData.scheduled_at)),
      });
      setNumber(editData.participant);
    }
  }, [editData]);

  useEffect(() => {
    if (did && participant) {
      form.setFieldsValue({
        did: did,
        participant: formatPhoneNumber(participant),
      });
      setNumber(participant);
    }
  }, []);

  const handleSubmit = (values: ScheduledMessage) => {
    const message: ScheduleMessage = {
      from: phoneNumberFormatE164(values.did),
      to: phoneNumberFormatE164(number),
      text: values.body,
      scheduled_at: new Date(values.scheduled_at).toISOString(),
      media: [],
    };

    if (new Date(values.scheduled_at) < new Date()) {
      messageApi.error("Scheduled time should be greater than current time");
      form.setFieldValue("scheduled_at", null);
      return;
    }

    if (editData && editData.id) {
      updateScheduledMessage(
        {
          data: {
            did: message.from,
            participant: message.to,
            body: message.text,
            scheduled_at: new Date(message.scheduled_at).toISOString(),
            media: [],
          },
          id: editData.id,
        },
        {
          onSuccess: (data) => {
            messageApi.success("Message updated successfully");
            dispatch(updateScheduledMessageSlice(data));
            setIsModalOpen(false);
          },
        }
      );
    } else {
      scheduleMessage(message, {
        onSuccess: (data) => {
          messageApi.success("Message scheduled successfully");
          dispatch(addScheduledMessageSlice(data));
          setIsModalOpen(false);
        },
      });
    }
  };

  return (
    <Form form={form} name="basic" style={{ padding: "10px" }} labelCol={{ span: 4 }} onFinish={handleSubmit}>
      <Form.Item<ScheduledMessage>
        label="From"
        name="did"
        rules={[{ required: true, message: "Please select a number" }]}
      >
        <Select
          showSearch
          placeholder="Select a number"
          optionFilterProp="children"
          style={{
            height: "fit-content",
          }}
          aria-errormessage="Please select a number"
          options={smsDids.filter(did => did.assigned).map((did) => {
            return {
              label: (
                <Flex vertical>
                  <Typography.Text>{formatPhoneNumber(did.did)}</Typography.Text>
                  <Tooltip title={did.label}>
                    <Typography.Text style={didlabelStyle}>{did.label}</Typography.Text>
                  </Tooltip>
                </Flex>
              ),
              value: did.did,
            };
          })}
        ></Select>
      </Form.Item>
      <Form.Item<ScheduledMessage>
        label="To"
        name="participant"
        rules={[
          {
            required: true,
            validateTrigger: "onFinish",
            message: "Please enter a valid phone number",
            validator: () => {
              return parsePhoneNumber(number, "US")?.isValid() ? Promise.resolve() : Promise.reject("Please enter a valid phone number");
            },
          },
        ]}
      >
        <InputWrapper>
          <Input
            placeholder="Enter phone number"
            value={formatPhoneNumber(number.replace(/ /g, ""))}
            onFocus={() => {
              setShowContacts(true);
            }}
            onBlur={(e) => {
              // Only close dropdown if focus is not moving to a dropdown element
              if (
              !e.relatedTarget ||
              !(e.relatedTarget as HTMLElement).closest('[data-contact-dropdown]')
              ) {
              setShowContacts(false);
              }
            }}
            onErrorCapture={() => {
              messageApi.error("Please enter a valid phone number");
            }}
            aria-errormessage="Please enter a valid phone number"
            onChange={(event) => {
              const inputValue = event.target.value
              try {
                const phoneNumber = parsePhoneNumber(inputValue, "US");
                if (phoneNumber?.country) {
                  setFlagIcon(phoneNumber.country);
                } else {
                  setFlagIcon("US");
                }
                if (phoneNumber?.isValid()) {
                  setNumber(phoneNumber.number);
                  form.setFieldValue("participant", phoneNumber.formatNational());
                } else {
                  setNumber(inputValue);
                }
              } catch (error) {
                setNumber(inputValue);
              }
              setShowContacts(inputValue.length > 0);
            }}
            addonBefore={<img style={{ height: "20px", width: "30px" }} src={`https://catamphetamine.gitlab.io/country-flag-icons/3x2/${flagIcon}.svg`}></img>}
          />
          {showContacts && filteredContacts.length > 0 && (
            <ContactDropdown>
              {filteredContacts.map((contact, index) => {
                const color = generateNameSpaceColor(contact.first_name);
                const fullName = formatContactName(contact);

                return (
                  <ContactItem
                    key={`${contact.id}-${index}`}
                    onMouseDown={() => handleContactSelect(contact)}
                  >
                    <Flex align="center">
                      <StyledAvatar
                        style={{ background: color }}
                        size="large"
                      >
                        {contact.first_name?.[0]?.toUpperCase() || '?'}
                      </StyledAvatar>
                      <Flex vertical>
                        <ContactName>{fullName}</ContactName>
                        <ContactPhone>
                          {formatPhoneNumber(contact.phones[0]?.phone_number)}
                        </ContactPhone>
                      </Flex>
                    </Flex>
                  </ContactItem>
                );
              })}
            </ContactDropdown>
          )}
        </InputWrapper>

      </Form.Item>

      <Form.Item label="Date" name="scheduled_at" rules={[{ required: true, message: "Please input scheduled date" }]}>
        <DatePicker
          className="ant-date-picker-schedule-message"
          showTime
          placeholder="Date and time"
          suffixIcon={null}
          aria-errormessage="Please select a valid date and time"
          format={"DD/MM/YYYY hh:mm A"}
          style={{ padding: "5px", cursor: "pointer !important", caretColor: "transparent", width: "100%" }}
          disabledDate={(current) => current < dayjs().startOf("day")}
          disabledTime={disabledTime}
        />
      </Form.Item>

      <Form.Item label="Message" name="body" rules={[{ required: true}]}>
        <TextArea aria-errormessage="Please enter a valid message" rows={4} placeholder="Enter Message" />
      </Form.Item>

      <Form.Item>
        <Button loading={isLoadingScheduleMessage || isLoadingUpdate} disabled={isLoadingScheduleMessage || isLoadingUpdate || form.getFieldsError().some(({ errors }) => errors.length)} style={{ width: "100%" }} type="primary" htmlType="submit">
          {editData ? "Update schedule" : "Schedule"}
        </Button>
      </Form.Item>
    </Form>
  );
};
